import React, { FC, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'

// components
import NotificationSection from '../../../../components/NotificationSection/NotificationSection.component'

// style components
import Text from '../../../../components/style/Text.style'
import {
    CirclesContainer,
    Circle,
    Icon,
    NumberInput,
    TimeSelectionSection,
    Title,
    DurationWarningTitle,
    DurationWarningText
} from './HourlyBooking.style'

// types
import { OpeningHoursObject } from '../../../../types/lot.interface'

// utils
import useParseOpeningHours from '../../../../utils/getOpeningHours.utils'

interface Props {
    isAfterOpeningStartHour: boolean
    selectedHour: number
    openingHours: OpeningHoursObject[] | undefined
    minimumDuration: number
    onChange: (value: number) => void
}

const HourlyBooking: FC<Props> = ({
    isAfterOpeningStartHour,
    selectedHour,
    openingHours,
    minimumDuration,
    onChange
}) => {
    const { t } = useTranslation()
    const { getDateOpeningHour, getStartAndEndHourOfDay } = useParseOpeningHours()

    const [max, setMax] = useState(0)
    const [showOpeningHoursNotification, setShowOpeningHoursNotification] = useState(false)
    const lastScrollTime = useRef(Date.now())
    const timeSelectionRef = useRef<HTMLDivElement>(null)
    const startAndEndHour = getStartAndEndHourOfDay(openingHours)

    useEffect(() => {
        const maxNumber = getDateOpeningHour(new Date(), openingHours)
        if (maxNumber === 1 || maxNumber === selectedHour) {
            setShowOpeningHoursNotification(true)
        }
        setMax(maxNumber)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedHour])

    const onNumberChange = (val: number) => {
        if (max === val) setShowOpeningHoursNotification(true)
        if (val > 0 && max >= val) {
            onChange(val)
            if (showOpeningHoursNotification) setShowOpeningHoursNotification(false)
        }
    }

    useEffect(() => {
        if (timeSelectionRef && timeSelectionRef.current) {
            timeSelectionRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            })
        }
    }, [])

    return (
        <>
            <TimeSelectionSection>
                <Title>{t('lot.hourlyBookingTitle')}</Title>
            </TimeSelectionSection>
            <CirclesContainer className="hide-input-arrows" ref={timeSelectionRef}>
                <Circle
                    onClick={() => {
                        if (selectedHour > 1 && minimumDuration < selectedHour) {
                            onNumberChange(selectedHour - 1)
                        }
                    }}
                >
                    <Icon
                        className="pkd-minus"
                        disabled={selectedHour > 1 && minimumDuration < selectedHour}
                        aria-label="minus"
                    />
                </Circle>
                <NumberInput
                    type="number"
                    value={selectedHour}
                    step={1}
                    min={minimumDuration}
                    onWheel={(e: any) => {
                        e.preventDefault()
                        const currentTime = Date.now()

                        if (currentTime - lastScrollTime.current > 200) {
                            // 200 ms delay between scrolls
                            if (e.deltaY < 0) {
                                const newValue = Math.min(selectedHour + 1, max)
                                onNumberChange(newValue)
                            } else {
                                const newValue = Math.max(selectedHour - 1, 1)
                                onNumberChange(newValue)
                            }
                            lastScrollTime.current = currentTime
                        }
                    }}
                    onChange={() => {}}
                />
                <Circle
                    onClick={() => {
                        if (isAfterOpeningStartHour && max > selectedHour) {
                            onNumberChange(selectedHour + 1)
                        }
                    }}
                >
                    <Icon
                        className="pkd-plus"
                        disabled={isAfterOpeningStartHour && max > selectedHour}
                        aria-label="plus"
                    />
                </Circle>
            </CirclesContainer>
            {/* todo zan: fonts */}
            <div style={{ textAlign: 'center', marginTop: '10px' }}>
                <Text
                    fontWeight={600}
                    typography={{
                        fontSize: '1.8rem',
                        lineHeight: '2.4rem'
                    }}
                >
                    {t(selectedHour > 1 ? 'common.hours' : 'common.hour')}
                </Text>
            </div>
            <DurationWarningTitle>{t('lot.beCareful')}</DurationWarningTitle>
            <DurationWarningText>{t('lot.lotHourlyWarning')}</DurationWarningText>
            {showOpeningHoursNotification && <NotificationSection text={t('lot.openingHoursMaximum')} />}
            {!isAfterOpeningStartHour && (
                <NotificationSection
                    backgroundColor="#FFC043"
                    text={t('lot.outsideOpeningHours', {
                        start: startAndEndHour.start_time,
                        end: startAndEndHour.end_time
                    })}
                />
            )}
        </>
    )
}

export default HourlyBooking
